import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Stepper, Step, StepLabel,
  Grid, TextField, MenuItem, Alert, IconButton, FormControlLabel, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import {
  emptySalaryStructure, salaryStructureToPayload, formatMoney,
  isValidQuotationPeriod, adjustEndDate,
  salaryStructureToForm, quotationEditBlock,
  MINIMUM_WAGE_CODE, minimumWageMessage,
  conditionsToForm, conditionsFromForm, changedConditions, invalidConditionFields,
} from './quotationUtils';
import { formatDateRange } from '../formatDate';
import { InfoTip } from '../LabelWithInfo';
import { SimulationView } from './SimulationBreakdown';
import QuotationConditionsEditor from './QuotationConditionsEditor';
import ErpDateField from '../ErpDateField';
import ErpTalanaSelect, { getTalanaCatalog } from '../ErpTalanaSelect';
import ErpAmountField from '../ErpAmountField';

// Razón social sugerida según la modalidad (decisión Erwin 2026-09-25): Grafton
// tiene dos en Talana — la EST para servicios transitorios y la otra para
// outsourcing. Solo es un pre-llenado; el usuario puede cambiarla.
const isEst = (item) => Boolean(item.extra?.empresaEst) || /\bEST\b/i.test(item.name || '');
const suggestLegalEntity = (items, mode) => {
  const active = (items || []).filter((i) => i.active !== false);
  if (active.length === 0) return '';
  const pick = active.length === 1
    ? active[0]
    : (mode === 'temporary_services' ? active.find(isEst) : active.find((i) => !isEst(i))) || active[0];
  return { uid: pick.uid, talanaId: pick.talanaId, name: pick.name, code: pick.code ?? null };
};

// Errores de configuración del cálculo (409): faltan parámetros del período
const SETUP_CODES = ['ENGINE_NOT_READY', 'PARAMETER_SET_MISSING', 'PARAMETER_SET_INCOMPLETE'];

/**
 * Wizard de cotización (PLAN_ERP.md §10.4, spec 3.3):
 * 1) modalidad + cliente + centro de costo + condiciones comerciales: las del
 *    acuerdo del centro de costo o, si no tiene, del de todo el cliente,
 *    editables (fase 4 del cotizador OUT/EST, 2026-10-13); a mano si el
 *    cliente no tiene acuerdo
 * 2) cargo (creación inline) + fechas del servicio
 * 3) estructura de renta
 * 4) simulación (calculadora inversa — motor pendiente del xlsm)
 * 5) resumen y guardar (queda en estado quoted)
 *
 * Modo edición (/erp/quotations/:quotationId/edit — fase 2 del cotizador
 * OUT/EST, 2026-10-13): mismo wizard pre-llenado sobre el PUT; solo mientras
 * la cotización no tenga contrato (si no, muestra el motivo). El cliente no
 * cambia; las condiciones comerciales se mantienen salvo que cambie la
 * modalidad o el centro de costo (ahí se vuelven a heredar). Una
 * subcotización solo cambia su fecha de ingreso (desde el
 * detalle). Al guardar, el backend vuelve a simular.
 */
const ErpQuotationWizard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken, showAlert } = useStore.getState();
  const { quotationId: editId } = useParams();
  const isEdit = Boolean(editId);

  // modo edición
  const [original, setOriginal] = useState(null); // cotización tal como está guardada
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [editBlock, setEditBlock] = useState(null); // { key, person } si no se puede editar
  const [subCount, setSubCount] = useState(0); // subcotizaciones de esta madre (aviso)
  const keepLegalEntityRef = useRef(isEdit); // no re-sugerir la razón social al cargar
  const initialCostCenterRef = useRef(null); // centro de costo a conservar al cargar

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // catálogos
  const [clients, setClients] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);

  // paso 1
  const [serviceMode, setServiceMode] = useState('temporary_services');
  const [legalEntity, setLegalEntity] = useState(''); // razón social Talana (ref)
  const [legalEntities, setLegalEntities] = useState([]);
  const [clientId, setClientId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  // Condiciones comerciales (fase 4 del cotizador OUT/EST, 2026-10-13): las
  // del acuerdo del centro de costo (o, si no tiene, del de todo el cliente),
  // editables en la cotización. inheritedSnapshot = lo que heredaría según
  // cliente, centro de costo y modalidad (GET /erp/quotations/commercial-conditions).
  const [inheritedSnapshot, setInheritedSnapshot] = useState(null);
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [conditionsError, setConditionsError] = useState(false);
  const [conditions, setConditions] = useState(() => conditionsToForm(null));
  const [conditionsEdited, setConditionsEdited] = useState(false);
  const conditionsRequestRef = useRef(0); // descarta respuestas de una consulta anterior

  // paso 2
  const [jobTitleId, setJobTitleId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [jobTitleDialogOpen, setJobTitleDialogOpen] = useState(false);
  const [newJobTitleName, setNewJobTitleName] = useState('');
  const [creatingJobTitle, setCreatingJobTitle] = useState(false);

  // paso 3
  const [salary, setSalary] = useState(emptySalaryStructure);

  // paso 4
  const [simulating, setSimulating] = useState(false);
  const [engineNotReady, setEngineNotReady] = useState(false);
  const [simulation, setSimulation] = useState(null);
  // Detalle del 422 de ingreso mínimo (fase 1b): bloquea avanzar hasta corregir
  const [minimumWageError, setMinimumWageError] = useState(null);
  // Cualquier cambio en la renta (montos, filas, modo), las fechas o la
  // modalidad deja sin efecto el aviso de la última simulación
  useEffect(() => {
    setMinimumWageError(null);
  }, [salary, startDate, endDate, serviceMode]);
  // Modo de cálculo excluyente: desde el sueldo base o desde la renta líquida
  // objetivo (calculadora inversa) — nunca ambos campos a la vez
  const [calcMode, setCalcMode] = useState('base');

  const handleCalcMode = (mode) => {
    if (!mode || mode === calcMode) return;
    setCalcMode(mode);
    // limpiar el campo del otro modo para que nunca viajen los dos llenos
    setSalaryField(mode === 'base' ? 'targetNetSalary' : 'baseSalary', '');
    setSimulation(null);
  };

  const headers = { headers: { Authorization: `Bearer ${authToken}` } };

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [clientsRes, jobTitlesRes] = await Promise.all([
          api.get('/erp/clients', { params: { organizationId: organization, status: 'active' }, ...headers }),
          api.get('/erp/job-titles', { params: { organizationId: organization, status: 'active' }, ...headers }),
        ]);
        setClients(clientsRes.data.response?.data || []);
        setJobTitles(jobTitlesRes.data.response?.data || []);
      } catch (err) {
        console.error(err);
        showAlert(t('erp.quotations.loadError'), 'error');
      }
    };
    if (organization) loadCatalogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // catálogo de razones sociales (para sugerir según la modalidad)
  useEffect(() => {
    if (!organization) return;
    getTalanaCatalog({ organization, authToken, type: 'legalEntities' })
      .then((items) => setLegalEntities(items || []))
      .catch(() => setLegalEntities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // al cambiar la modalidad (o cargar el catálogo): sugerir la razón social.
  // Editando se conserva la de la cotización hasta que se cambie la modalidad;
  // una cotización anterior sin razón social recibe la sugerida, como una nueva
  // (`original` en las dependencias: al terminar de cargarla). Ajuste 2026-10-14.
  useEffect(() => {
    if (keepLegalEntityRef.current) return;
    setLegalEntity(suggestLegalEntity(legalEntities, serviceMode));
  }, [legalEntities, serviceMode, original]);

  // al elegir cliente: cargar sus centros de costo
  useEffect(() => {
    const loadCostCenters = async () => {
      setCostCenterId('');
      if (!clientId) { setCostCenters([]); return; }
      let centers = [];
      try {
        const response = await api.get(`/erp/clients/${clientId}/cost-centers`, { params: { organizationId: organization }, ...headers });
        centers = (response.data.response?.data || []).filter((c) => c.status === 'active');
      } catch (err) {
        console.error(err);
      }
      // Edición: se conserva el centro de costo de la cotización (aunque hoy
      // esté inactivo o la lista no cargue)
      const keep = initialCostCenterRef.current;
      initialCostCenterRef.current = null;
      if (keep && !centers.some((c) => c.uid === keep.uid)) centers.push(keep);
      setCostCenters(centers);
      if (keep) setCostCenterId(keep.uid);
    };
    loadCostCenters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Condiciones que heredaría la cotización: el acuerdo puede ser por centro
  // de costo, así que se consultan con cliente + centro de costo + modalidad
  useEffect(() => {
    conditionsRequestRef.current += 1;
    const requestId = conditionsRequestRef.current;
    setInheritedSnapshot(null);
    setConditionsError(false);
    if (!organization || !clientId || !costCenterId) {
      setConditionsLoading(false);
      return;
    }
    setConditionsLoading(true);
    api.get('/erp/quotations/commercial-conditions', {
      params: { organizationId: organization, clientId, costCenterId, serviceMode },
      ...headers,
    })
      .then((response) => {
        if (conditionsRequestRef.current !== requestId) return;
        const snapshot = response.data.response?.data?.snapshot || null;
        setInheritedSnapshot(snapshot);
        setConditionsError(!snapshot);
      })
      .catch((err) => {
        if (conditionsRequestRef.current !== requestId) return;
        console.error(err);
        setConditionsError(true);
      })
      .finally(() => {
        if (conditionsRequestRef.current === requestId) setConditionsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, clientId, costCenterId, serviceMode]);

  // Modo edición: cargar la cotización (y su familia, para saber si tiene
  // contrato o subcotizaciones) y pre-llenar el wizard
  useEffect(() => {
    if (!isEdit || !organization) return;
    const loadForEdit = async () => {
      try {
        setLoadingEdit(true);
        setEditBlock(null);
        const params = { params: { organizationId: organization }, ...headers };
        const [quotationRes, familyRes] = await Promise.all([
          api.get(`/erp/quotations/${editId}`, params),
          api.get(`/erp/quotations/${editId}/family`, params).catch(() => null),
        ]);
        const q = quotationRes.data.response?.data;
        if (!q) {
          setEditBlock({ key: 'loadError', person: '' });
          return;
        }
        const family = familyRes?.data?.response?.data || null;
        const members = family ? [family.root, ...(family.subQuotations || [])] : [];
        const block = quotationEditBlock(q, members.find((m) => m.uid === q.uid)?.contracts);
        if (block) {
          setEditBlock(block);
          return;
        }
        // Subcotización: solo cambia su fecha de ingreso, en el detalle
        if (q.parentQuotationId) {
          navigate(`/erp/quotations/${q.uid}`, { replace: true, state: { openEntryDialog: true } });
          return;
        }
        setOriginal(q);
        setSubCount(family && family.root.uid === q.uid ? (family.subQuotations || []).length : 0);
        setServiceMode(q.serviceMode || 'temporary_services');
        // sin razón social (cotizaciones anteriores a ella): se sugiere según la modalidad
        keepLegalEntityRef.current = Boolean(q.legalEntity);
        setLegalEntity(q.legalEntity || '');
        initialCostCenterRef.current = q.costCenterId
          ? { uid: q.costCenterId, code: '', name: q.costCenterName || '', status: 'active' }
          : null;
        setClientId(q.clientId || '');
        setJobTitleId(q.jobTitleId || '');
        setStartDate(q.period?.startDate || '');
        setEndDate(q.period?.endDate || '');
        // Un solo modo de cálculo: desde el líquido solo sin sueldo base (igual
        // que el backend). Una cotización anterior podía traer los dos: el
        // cálculo usa el base, así que el líquido objetivo no se muestra ni se
        // vuelve a guardar. Ajuste 2026-10-14.
        const fromTarget = Boolean(q.salaryStructure?.targetNetSalary && !q.salaryStructure?.baseSalary);
        const salaryForm = salaryStructureToForm(q.salaryStructure);
        setSalary(fromTarget ? { ...salaryForm, baseSalary: '' } : { ...salaryForm, targetNetSalary: '' });
        setCalcMode(fromTarget ? 'target' : 'base');
        // Las condiciones comerciales pasan al formulario cuando se sabe de
        // dónde salen (conditionsMode, más abajo)
      } catch (err) {
        console.error(err);
        setEditBlock({ key: 'loadError', person: '' });
      } finally {
        setLoadingEdit(false);
      }
    };
    loadForEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId, organization]);

  // De dónde salen las condiciones comerciales (fase 4):
  // - 'keep': editando con la misma modalidad y centro de costo se MANTIENEN
  //   las guardadas (también si el acuerdo no se pudo consultar), salvo una
  //   cotización manual cuyo cliente ya tiene acuerdo: esa vuelve a heredar
  // - 'agreement': las del acuerdo del centro de costo o de todo el cliente
  // - 'manual': el cliente no tiene acuerdo y se ingresan a mano
  // null mientras se consultan (o si no se pudieron consultar)
  const sameScopeAsOriginal = Boolean(isEdit && original
    && original.serviceMode === serviceMode && (original.costCenterId || '') === costCenterId);
  const manualNowWithAgreement = Boolean(original?.commercialSnapshot?.manual
    && inheritedSnapshot && !inheritedSnapshot.manual);
  let conditionsMode = null;
  if (clientId && costCenterId) {
    if (sameScopeAsOriginal && !manualNowWithAgreement) conditionsMode = 'keep';
    else if (inheritedSnapshot) conditionsMode = inheritedSnapshot.manual ? 'manual' : 'agreement';
  }
  // Punto de partida del formulario: lo guardado ('keep') o lo heredado
  const baselineSnapshot = conditionsMode === 'keep' ? original?.commercialSnapshot || null : inheritedSnapshot;

  // Cada vez que cambia de dónde salen (cliente, centro de costo, modalidad o
  // la cotización cargada), el formulario vuelve a partir de ahí; una
  // simulación hecha con las condiciones anteriores deja de valer
  useEffect(() => {
    setConditions(conditionsToForm(baselineSnapshot));
    setConditionsEdited(false);
    setSimulation(null);
  }, [conditionsMode, baselineSnapshot]);

  const effectiveConditions = conditionsFromForm(conditions);
  // Lo cambiado en el wizard respecto del punto de partida: viaja como
  // commercialOverrides (el backend lo aplica y lo marca contra lo heredado)
  const editedKeys = conditionsMode && conditionsMode !== 'manual'
    ? changedConditions(effectiveConditions, conditionsFromForm(conditionsToForm(baselineSnapshot)))
    : [];
  // Orden de compra: se ingresa a mano solo sin acuerdo (si no, la fija el acuerdo)
  const purchaseOrderEditable = conditionsMode === 'manual' || (conditionsMode === 'keep' && Boolean(baselineSnapshot?.manual));
  const overrideEntries = editedKeys.map((key) => [key, effectiveConditions[key]]);
  if (conditionsMode === 'keep' && purchaseOrderEditable
    && effectiveConditions.usesPurchaseOrder !== Boolean(baselineSnapshot?.usesPurchaseOrder)) {
    overrideEntries.push(['usesPurchaseOrder', effectiveConditions.usesPurchaseOrder]);
  }
  const conditionOverrides = overrideEntries.length > 0 ? Object.fromEntries(overrideEntries) : null;
  // Marcas "Modificado": respecto del acuerdo. Con las condiciones guardadas,
  // respecto de lo heredado al cotizar (o de lo guardado, si la cotización es
  // anterior a la fase 4). Sin acuerdo no hay con qué comparar.
  const markReference = conditionsMode === 'agreement'
    ? inheritedSnapshot
    : conditionsMode === 'keep' && baselineSnapshot && !baselineSnapshot.manual
      ? baselineSnapshot.inherited || baselineSnapshot
      : null;
  const modifiedKeys = markReference
    ? changedConditions(effectiveConditions, conditionsFromForm(conditionsToForm(markReference)))
    : [];

  const handleConditionsChange = (next) => {
    setConditions(next);
    setConditionsEdited(true);
    setSimulation(null);
  };
  const resetConditions = () => {
    setConditions(conditionsToForm(baselineSnapshot));
    setConditionsEdited(false);
    setSimulation(null);
  };

  // Opciones de los selects: editando, el cliente y el cargo de la cotización
  // aunque hoy estén inactivos
  const clientOptions = original && original.clientId && !clients.some((c) => c.uid === original.clientId)
    ? [...clients, { uid: original.clientId, legalName: original.clientName || original.clientId }]
    : clients;
  const jobTitleOptions = original && original.jobTitleId && !jobTitles.some((j) => j.uid === original.jobTitleId)
    ? [...jobTitles, { uid: original.jobTitleId, name: original.jobTitleName || original.jobTitleId }]
    : jobTitles;

  const canNext = () => {
    // Sin saber de dónde salen las condiciones no se avanza (editando con la
    // misma modalidad y centro de costo se mantienen las guardadas)
    // Montos de las condiciones que no se entienden: tampoco
    if (activeStep === 0) {
      return Boolean(serviceMode && legalEntity && clientId && costCenterId && conditionsMode && !conditionsLoading)
        && invalidConditionFields(conditions).length === 0;
    }
    if (activeStep === 1) return Boolean(jobTitleId && isValidQuotationPeriod(startDate, endDate));
    if (activeStep === 2) return calcMode === 'base' ? salary.baseSalary !== '' : salary.targetNetSalary !== '';
    // Simulación bajo el ingreso mínimo (fase 1b): no se avanza hasta corregir la renta
    if (activeStep === 3) return !minimumWageError;
    return true;
  };

  const handleCreateJobTitle = async () => {
    if (!newJobTitleName.trim()) return;
    try {
      setCreatingJobTitle(true);
      const response = await api.post(
        '/erp/job-titles',
        { organizationId: organization, name: newJobTitleName.trim() },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        const created = response.data.response.data;
        setJobTitles((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
        setJobTitleId(created.uid);
        setJobTitleDialogOpen(false);
        setNewJobTitleName('');
        showAlert(t('erp.jobTitles.saveSuccess'), 'success');
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errorMessage || '';
      showAlert(
        message.includes('already exists') ? t('erp.jobTitles.duplicateName') : t('erp.jobTitles.saveError'),
        'error'
      );
    } finally {
      setCreatingJobTitle(false);
    }
  };

  // Condiciones para simular: con acuerdo ('agreement') las arma el backend
  // desde el acuerdo (cliente + centro de costo) con los cambios del wizard,
  // igual que al guardar; 'keep': las guardadas con los cambios; 'manual':
  // las del formulario
  const simulationConditions = () => {
    if (conditionsMode === 'keep') {
      return { commercialConditions: { ...(original?.commercialSnapshot || {}), ...(conditionOverrides || {}) } };
    }
    if (conditionsMode === 'manual') return { commercialConditions: effectiveConditions };
    return { commercialConditions: null, commercialOverrides: conditionOverrides };
  };

  const handleSimulate = async () => {
    try {
      setSimulating(true);
      setEngineNotReady(false);
      setMinimumWageError(null);
      const response = await api.post(
        '/erp/quotations/simulate',
        {
          organizationId: organization,
          serviceMode,
          clientId, // con acuerdo vigente, el backend hereda de él
          costCenterId, // fase 4: el acuerdo puede ser del centro de costo
          period: { startDate, endDate },
          salaryStructure: salaryStructureToPayload(salary),
          ...simulationConditions(),
        },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        setSimulation(response.data.response.data);
      }
    } catch (err) {
      const code = err.response?.data?.response?.code;
      if (code === MINIMUM_WAGE_CODE) {
        setSimulation(null);
        setMinimumWageError(err.response.data.response);
      } else if (SETUP_CODES.includes(code)) {
        setEngineNotReady(true);
      } else {
        console.error(err);
        showAlert(t('erp.quotations.simulationError'), 'error');
      }
    } finally {
      setSimulating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const body = {
        organizationId: organization,
        costCenterId,
        jobTitleId,
        serviceMode,
        legalEntity,
        period: { startDate, endDate },
        // editando se conservan los campos que el wizard no maneja
        salaryStructure: isEdit
          ? { ...(original?.salaryStructure || {}), ...salaryStructureToPayload(salary) }
          : salaryStructureToPayload(salary),
      };
      if (!isEdit) body.clientId = clientId;
      // Condiciones: sin acuerdo van completas (snapshot manual); con acuerdo o
      // con las guardadas, solo lo cambiado en el wizard (commercialOverrides)
      if (conditionsMode === 'manual') {
        body.commercialSnapshot = effectiveConditions;
      } else {
        if (isEdit && conditionsMode === 'agreement') body.refreshCommercialSnapshot = true;
        if (conditionOverrides) body.commercialOverrides = conditionOverrides;
      }
      const response = isEdit
        ? await api.put(`/erp/quotations/${editId}`, body, headers)
        : await api.post('/erp/quotations', body, headers);
      if (response.data.response?.status_response === 'OK') {
        if (isEdit && subCount > 0) {
          showAlert(t('erp.quotations.editSubsWarning', { count: subCount }), 'warning');
        } else {
          showAlert(t(isEdit ? 'erp.quotations.updateSuccess' : 'erp.quotations.createSuccess'), 'success');
        }
        const uid = isEdit ? editId : response.data.response.data?.uid;
        navigate(uid ? `/erp/quotations/${uid}` : '/erp/quotations');
      } else {
        showAlert(t('erp.quotations.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      let message = t('erp.quotations.saveError');
      if (code === 'QUOTATION_HAS_CONTRACT') message = t('erp.quotations.editBlocked.contractNoName');
      else if (code === MINIMUM_WAGE_CODE) {
        setMinimumWageError(err.response.data.response); // también bloquea el paso de simulación
        message = `${minimumWageMessage(t, err.response.data.response)} ${t('erp.quotations.minimumWage.fixInWizard')}`;
      }
      showAlert(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const setSalaryField = (field, value) => setSalary((p) => ({ ...p, [field]: value }));

  const setRow = (listField, index, key, value) =>
    setSalary((p) => ({
      ...p,
      [listField]: p[listField].map((r, i) => (i === index ? { ...r, [key]: value } : r)),
    }));

  const addRow = (listField) =>
    setSalary((p) => ({ ...p, [listField]: [...p[listField], { name: '', amount: '' }] }));

  const removeRow = (listField, index) =>
    setSalary((p) => ({ ...p, [listField]: p[listField].filter((_, i) => i !== index) }));

  const dynamicRows = (listField, titleKey, tooltipKey) => (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t(titleKey)}
        {tooltipKey && <InfoTip title={t(tooltipKey)} />}
      </Typography>
      {salary[listField].map((row, index) => (
        <Grid container spacing={1} key={index} sx={{ mb: 1 }} alignItems="center">
          <Grid item xs={6}>
            <TextField fullWidth size="small" label={t('erp.quotations.form.itemName')}
              value={row.name} onChange={(e) => setRow(listField, index, 'name', e.target.value)} />
          </Grid>
          <Grid item xs={4}>
            <ErpAmountField label={t('erp.quotations.form.itemAmount')}
              value={row.amount} onChange={(e) => setRow(listField, index, 'amount', e.target.value)} />
          </Grid>
          <Grid item xs={2}>
            <IconButton size="small" onClick={() => removeRow(listField, index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={() => addRow(listField)} sx={{ textTransform: 'none' }}>
        {t('erp.quotations.form.addItem')}
      </Button>
    </Grid>
  );

  const steps = [1, 2, 3, 4, 5].map((n) => t(`erp.quotations.steps.step${n}`));

  const selectedClient = clientOptions.find((c) => c.uid === clientId);
  const selectedCostCenter = costCenters.find((c) => c.uid === costCenterId);
  const selectedJobTitle = jobTitleOptions.find((j) => j.uid === jobTitleId);
  const costCenterLabel = (c) => (c ? (c.code ? `${c.code} — ${c.name}` : c.name) : '');

  // Origen de las condiciones (paso 1) y su resumen (paso 5)
  let conditionsSource = null;
  if (conditionsMode === 'keep') {
    conditionsSource = conditionsError
      ? { severity: 'warning', text: t('erp.quotations.conditions.keepError') }
      : { severity: 'info', text: t('erp.quotations.conditions.keep') };
  } else if (conditionsMode === 'manual') {
    conditionsSource = { severity: 'warning', text: t('erp.quotations.conditions.manual') };
  } else if (conditionsMode === 'agreement') {
    conditionsSource = inheritedSnapshot?.agreementScope === 'costCenter'
      ? {
          severity: 'success',
          text: t('erp.quotations.conditions.fromCostCenter', {
            costCenter: inheritedSnapshot.agreementCostCenterName || costCenterLabel(selectedCostCenter),
          }),
        }
      : { severity: 'success', text: t('erp.quotations.conditions.fromClient') };
  }
  const conditionsOrigin = conditionsMode === 'manual' || (conditionsMode === 'keep' && baselineSnapshot?.manual)
    ? t('erp.quotations.inheritance.manualShort')
    : conditionsMode === 'keep'
      ? t('erp.quotations.conditions.summaryKeep')
      : inheritedSnapshot?.agreementScope === 'costCenter'
        ? t('erp.quotations.conditions.summaryCostCenter')
        : t('erp.quotations.conditions.summaryClient');
  const conditionsSummary = modifiedKeys.length > 0
    ? `${conditionsOrigin} · ${t('erp.quotations.conditions.modifiedCount', { count: modifiedKeys.length })}`
    : conditionsOrigin;
  const marginSummary = effectiveConditions.margin && effectiveConditions.margin.value !== null
    ? (effectiveConditions.margin.feeType === 'percentage'
      ? `${String(effectiveConditions.margin.value).replace('.', ',')}%`
      : formatMoney(effectiveConditions.margin.value))
    : '—';

  const backButton = (
    <Button startIcon={<BackIcon />} sx={{ textTransform: 'none', mb: 1 }}
      onClick={() => navigate(isEdit ? `/erp/quotations/${editId}` : '/erp/quotations')}>
      {t('erp.quotations.title')}
    </Button>
  );

  if (isEdit && loadingEdit) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // No se puede editar: el motivo (con contrato, archivada) y vuelta al detalle
  if (isEdit && editBlock) {
    return (
      <Box sx={{ p: 3 }}>
        {backButton}
        <Alert severity={editBlock.key === 'loadError' ? 'error' : 'warning'} sx={{ mb: 2 }}>
          {editBlock.key === 'loadError'
            ? t('erp.quotations.loadError')
            : t(`erp.quotations.editBlocked.${editBlock.key}`, { person: editBlock.person })}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(`/erp/quotations/${editId}`)} sx={{ textTransform: 'none' }}>
          {t('erp.quotations.goToDetail')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {backButton}

      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        {isEdit
          ? t('erp.quotations.editTitle', { number: original?.quotationNumber || '' })
          : t('erp.quotations.createTitle')}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3 }}>
        {activeStep === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField select fullWidth size="small" label={t('erp.quotations.form.serviceMode')}
                helperText={t('erp.tooltips.quotation.serviceMode')}
                value={serviceMode}
                onChange={(e) => { keepLegalEntityRef.current = false; setServiceMode(e.target.value); }}>
                <MenuItem value="temporary_services">{t('erp.quotations.serviceMode.temporary_services')}</MenuItem>
                <MenuItem value="outsourcing">{t('erp.quotations.serviceMode.outsourcing')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <ErpTalanaSelect type="legalEntities" label={t('erp.quotations.form.legalEntity')}
                value={legalEntity} onChange={(ref) => setLegalEntity(ref || '')} required
                helperText={t('erp.quotations.form.legalEntityHelper')} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select fullWidth size="small" label={t('erp.quotations.form.client')}
                value={clientId} onChange={(e) => setClientId(e.target.value)}
                disabled={isEdit} helperText={isEdit ? t('erp.quotations.editClientLocked') : ''}>
                {clientOptions.map((c) => (
                  <MenuItem key={c.uid} value={c.uid}>{c.legalName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select fullWidth size="small" label={t('erp.quotations.form.costCenter')}
                value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}
                disabled={!clientId}
                helperText={clientId && costCenters.length === 0 ? t('erp.quotations.form.noCostCenters') : ''}>
                {costCenters.map((c) => (
                  <MenuItem key={c.uid} value={c.uid}>{costCenterLabel(c)}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Condiciones comerciales (fase 4 del cotizador OUT/EST): heredadas del
                acuerdo del centro de costo o del cliente y editables; lo cambiado
                respecto del acuerdo queda marcado */}
            {clientId && costCenterId && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  {t('erp.quotations.conditions.title')}
                </Typography>
                {/* Mientras se consulta no se edita: la respuesta puede cambiar de
                    dónde salen (cotización manual cuyo cliente ya tiene acuerdo) */}
                {conditionsLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">{t('erp.quotations.conditions.loading')}</Typography>
                  </Box>
                )}
                {!conditionsMode && !conditionsLoading && conditionsError && (
                  <Alert severity="error">{t('erp.quotations.agreementLoadError')}</Alert>
                )}
                {conditionsMode && conditionsSource && !conditionsLoading && (
                  <>
                    <Alert severity={conditionsSource.severity} sx={{ mb: 2 }}>{conditionsSource.text}</Alert>
                    <QuotationConditionsEditor value={conditions} onChange={handleConditionsChange}
                      modified={modifiedKeys} showPurchaseOrder={purchaseOrderEditable} />
                    {conditionsEdited && (
                      <Button size="small" onClick={resetConditions} sx={{ textTransform: 'none', mt: 1 }}>
                        {t('erp.quotations.conditions.reset')}
                      </Button>
                    )}
                  </>
                )}
              </Grid>
            )}
          </Grid>
        )}

        {activeStep === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField select fullWidth size="small" label={t('erp.quotations.form.jobTitle')}
                  helperText={t('erp.tooltips.quotation.jobTitle')}
                  value={jobTitleId} onChange={(e) => setJobTitleId(e.target.value)}>
                  {jobTitleOptions.map((j) => (
                    <MenuItem key={j.uid} value={j.uid}>{j.name}</MenuItem>
                  ))}
                </TextField>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setJobTitleDialogOpen(true)}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                  {t('erp.quotations.form.newJobTitle')}
                </Button>
              </Box>
            </Grid>
            {/* Fecha de ingreso (fase 2) y término: por defecto fin del mes de
                ingreso; desde el 2026-10-31 puede abarcar varios meses (mes de
                ingreso, meses completos y mes de término). La simulación trae
                además el mes completo (contrato) */}
            <Grid item xs={6} sm={3}>
              <ErpDateField label={t('erp.quotations.form.entryDate')} value={startDate}
                helperText={t('erp.quotations.form.entryDateHint')}
                onChange={(value) => {
                  setStartDate(value);
                  setEndDate((prev) => adjustEndDate(value, prev, startDate));
                  // Una simulación con otras fechas deja de valer (el resumen
                  // muestra el período: no puede quedar con montos de otro)
                  setSimulation(null);
                }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ErpDateField label={t('erp.quotations.form.periodUntil')} value={endDate}
                onChange={(value) => { setEndDate(value); setSimulation(null); }}
                disabled={!startDate} minDate={startDate || undefined}
                helperText={t('erp.quotations.form.periodUntilHint')} />
            </Grid>
          </Grid>
        )}

        {activeStep === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ToggleButtonGroup exclusive size="small" color="primary" value={calcMode}
                onChange={(e, mode) => handleCalcMode(mode)}>
                <ToggleButton value="base" sx={{ textTransform: 'none' }}>
                  {t('erp.quotations.form.calcModeBase')}
                </ToggleButton>
                <ToggleButton value="target" sx={{ textTransform: 'none' }}>
                  {t('erp.quotations.form.calcModeTarget')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            {calcMode === 'base' ? (
              <Grid item xs={6} sm={3}>
                <ErpAmountField label={t('erp.quotations.form.baseSalary')}
                  value={salary.baseSalary} onChange={(e) => setSalaryField('baseSalary', e.target.value)} />
              </Grid>
            ) : (
              <Grid item xs={6} sm={3}>
                <ErpAmountField label={t('erp.quotations.form.targetNetSalary')}
                  helperText={t('erp.quotations.form.calcModeTargetHint')}
                  value={salary.targetNetSalary} onChange={(e) => setSalaryField('targetNetSalary', e.target.value)} />
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label={t('erp.quotations.form.gratificationType')}
                helperText={t('erp.tooltips.quotation.gratificationType')}
                value={salary.gratificationType} onChange={(e) => setSalaryField('gratificationType', e.target.value)}>
                <MenuItem value="legal_monthly">{t('erp.quotations.gratification.legal_monthly')}</MenuItem>
                <MenuItem value="legal_annual">{t('erp.quotations.gratification.legal_annual')}</MenuItem>
                <MenuItem value="none">{t('erp.quotations.gratification.none')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <ErpAmountField label={t('erp.quotations.form.mealAllowance')}
                helperText={t('erp.tooltips.quotation.mealAllowance')}
                value={salary.mealAllowance} onChange={(e) => setSalaryField('mealAllowance', e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ErpAmountField label={t('erp.quotations.form.transportAllowance')}
                helperText={t('erp.tooltips.quotation.transportAllowance')}
                value={salary.transportAllowance} onChange={(e) => setSalaryField('transportAllowance', e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ErpAmountField label={t('erp.quotations.form.teleworkAllowance')}
                helperText={t('erp.tooltips.quotation.teleworkAllowance')}
                value={salary.teleworkAllowance} onChange={(e) => setSalaryField('teleworkAllowance', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={salary.vacationProvision !== false}
                  onChange={(e) => setSalaryField('vacationProvision', e.target.checked)} />}
                label={(<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{t('erp.quotations.form.vacationProvision')}<InfoTip title={t('erp.tooltips.quotation.vacationProvision')} /></Box>)} />
            </Grid>
            {/* Monto mensual pactado (fase 1b): vacío = automático, imponible / 30 × 1,75 */}
            <Grid item xs={12} sm={6}>
              <ErpAmountField label={t('erp.quotations.form.vacationProvisionAmount')}
                helperText={t('erp.quotations.form.vacationProvisionAmountHint')}
                disabled={salary.vacationProvision === false}
                value={salary.vacationProvisionAmount}
                onChange={(e) => setSalaryField('vacationProvisionAmount', e.target.value)} />
            </Grid>
            {dynamicRows('taxableBonuses', 'erp.quotations.form.taxableBonuses', 'erp.tooltips.quotation.taxableBonuses')}
            {dynamicRows('otherAllowances', 'erp.quotations.form.otherAllowances', 'erp.tooltips.quotation.otherAllowances')}
            {dynamicRows('additionalProvisions', 'erp.quotations.form.additionalProvisions', 'erp.tooltips.quotation.additionalProvisions')}
          </Grid>
        )}

        {activeStep === 3 && (
          <Box>
            <Button
              variant="contained"
              startIcon={simulating ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
              disabled={simulating}
              onClick={handleSimulate}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none', mb: 2 }}
            >
              {t('erp.quotations.simulate')}
            </Button>

            {engineNotReady && (
              <Alert severity="warning" sx={{ mb: 2 }}>{t('erp.quotations.parameterSetMissing')}</Alert>
            )}

            {minimumWageError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {minimumWageMessage(t, minimumWageError)} {t('erp.quotations.minimumWage.fixInWizard')}
              </Alert>
            )}

            {simulation && (
              <>
                {/* Con mes completo de referencia (período parcial o de varios
                    meses): el mensual y el total del período (2026-10-31) */}
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t('erp.quotations.simulationOk')} — {simulation.monthly
                    ? t('erp.quotations.sim.summaryMonthlyAndPeriod', {
                        net: formatMoney(simulation.monthly.netSalary),
                        monthly: formatMoney(simulation.monthly.invoiceTotal),
                        total: formatMoney(simulation.invoiceTotal),
                        period: formatDateRange(startDate, endDate),
                      })
                    : `${t('erp.quotations.sim.netSalary')}: ${formatMoney(simulation.netSalary)} · ${t('erp.quotations.sim.invoiceTotal')}: ${formatMoney(simulation.invoiceTotal)}`}
                </Alert>
                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 2 }}>
                  <SimulationView simulation={simulation} period={{ startDate, endDate }} />
                </Box>
              </>
            )}

            <Typography variant="body2" color="text.secondary">
              {t('erp.quotations.simulationHint')}
            </Typography>
          </Box>
        )}

        {activeStep === 4 && (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={700}>{t('erp.quotations.summary.title')}</Typography>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>{t('erp.quotations.form.client')}:</strong> {selectedClient?.legalName || '—'}<br />
                <strong>{t('erp.quotations.form.costCenter')}:</strong> {selectedCostCenter ? costCenterLabel(selectedCostCenter) : '—'}<br />
                <strong>{t('erp.quotations.form.jobTitle')}:</strong> {selectedJobTitle?.name || '—'}<br />
                <strong>{t('erp.quotations.form.serviceMode')}:</strong> {t(`erp.quotations.serviceMode.${serviceMode}`)}<br />
                <strong>{t('erp.quotations.form.legalEntity')}:</strong> {legalEntity?.name || '—'}<br />
                <strong>{t('erp.quotations.table.period')}:</strong> {formatDateRange(startDate, endDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>{t('erp.quotations.form.baseSalary')}:</strong> {formatMoney(salary.baseSalary)}<br />
                <strong>{t('erp.quotations.form.targetNetSalary')}:</strong> {formatMoney(salary.targetNetSalary)}<br />
                <strong>{t('erp.quotations.inheritance.label')}:</strong> {conditionsSummary}<br />
                <strong>{t('erp.quotations.conditions.margin')}:</strong> {marginSummary}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">{t(isEdit ? 'erp.quotations.editNote' : 'erp.quotations.summary.note')}</Alert>
            </Grid>
            {isEdit && subCount > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">{t('erp.quotations.editSubsWarning', { count: subCount })}</Alert>
              </Grid>
            )}
          </Grid>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)} sx={{ textTransform: 'none' }}>
            {t('erp.quotations.back')}
          </Button>
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" disabled={!canNext()} onClick={() => setActiveStep((s) => s + 1)}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
              {t('erp.quotations.next')}
            </Button>
          ) : (
            <Button variant="contained" disabled={saving} onClick={handleSave}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
              {saving ? t('erp.common.saving') : t(isEdit ? 'erp.common.save' : 'erp.quotations.saveQuotation')}
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog open={jobTitleDialogOpen} onClose={() => setJobTitleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('erp.jobTitles.add')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <TextField fullWidth size="small" autoFocus label={t('erp.jobTitles.form.name')}
            value={newJobTitleName} onChange={(e) => setNewJobTitleName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobTitleDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" disabled={creatingJobTitle} onClick={handleCreateJobTitle}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
            {creatingJobTitle ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpQuotationWizard;
